import { Checkbox, Flex, FormControl, useToast, Text } from '@chakra-ui/react';
import {
  organizationApi,
  UpdateOrgRequest,
} from '@texas/api/endpoints/organizationApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { SubmitButton } from '@texas/components/shared/form/SubmitButton';
import { TexasFormLabel } from '@texas/components/shared/form/TexasFormLabel';
import { useActiveSession } from '@texas/hooks/useSession';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export function UpdateSyncGarp() {
  const session = useActiveSession();
  const { data, refetch } = useApiResource(organizationApi.get);
  const updateRequest = useApiRequest(organizationApi.update);
  const toast = useToast();
  const { t } = useTranslation();

  useEffect(() => {
    refetch(session.currentUser.organizationId);
  }, [refetch, session.currentUser.organizationId]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UpdateOrgRequest>();

  useEffect(() => {
    if (!data) return;
    reset({
      enableGarpSync: data.enableGarpSync,
    });
  }, [data, reset]);

  const onSubmit = async (data: UpdateOrgRequest) =>
    await request(
      updateRequest.request,
      [session.currentUser.organizationId, data],
      () => {
        toast({
          title: t('general.updated'),
          status: 'success',
          isClosable: true,
        });
      },
    );

  return (
    <Flex direction="column" gap={4} maxW="300px">
      {updateRequest.error && <ErrorDetails error={updateRequest.error} />}
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
        <Flex flexDir="column" gap={4}>
          <Text variant="header">{t('garp.disableSyncTitle')}</Text>
          <Text>{t('garp.disableSyncDesc')}</Text>
          <FormControl isInvalid={!!errors.enableGarpSync}>
            <Flex gap={0} alignItems="center">
              <TexasFormLabel>{t('garp.disableSyncToggle')}</TexasFormLabel>
              <Controller
                name="enableGarpSync"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Checkbox onChange={onChange} isChecked={value} />
                )}
              />
            </Flex>

            <ErrorLabel text={errors.enableGarpSync?.message} />
          </FormControl>

          <SubmitButton loading={isSubmitting}>
            {t('general.update')}
          </SubmitButton>
        </Flex>
      </form>
    </Flex>
  );
}
